import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ClipboardCheck, AlertTriangle, Clock, ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { renderMentions } from "@/lib/mentions.jsx";

/**
 * Parser simples: detecta "hoje HH[h|:]mm" e "amanhã HH[h|:]mm" no texto e retorna {dueDate, cleanText}
 */
function parseDueFromText(text) {
  let dueDate = null;
  const lower = text.toLowerCase();
  const timeRe = /(\d{1,2})[:h](\d{0,2})/;
  const today = lower.includes("hoje");
  const tomorrow = lower.includes("amanhã") || lower.includes("amanha");
  const tm = text.match(timeRe);
  if ((today || tomorrow) && tm) {
    const d = new Date();
    if (tomorrow) d.setDate(d.getDate() + 1);
    const h = parseInt(tm[1], 10) || 0;
    const m = parseInt(tm[2] || "0", 10) || 0;
    d.setHours(h, m, 0, 0);
    dueDate = d.toISOString();
  }
  return { dueDate, cleanText: text };
}

export default function TasksWidget({ apiCall, scope = "all", title = "Tarefas do dia", testId = "tasks-widget" }) {
  const [summary, setSummary] = useState({ overdue: [], today: [], no_date: [], total_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [quickText, setQuickText] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = scope === "mine" ? "/tasks/today/summary?scope=mine" : "/tasks/today/summary";
      const res = await apiCall("get", url);
      setSummary(res.data || { overdue: [], today: [], no_date: [], total_pending: 0 });
    } catch {
      /* silencioso */
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [scope]);

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!quickText.trim()) return;
    setAdding(true);
    try {
      const { dueDate } = parseDueFromText(quickText);
      const priorityMatch = quickText.match(/!(alta|urgente|baixa)/i);
      const priority = priorityMatch
        ? (priorityMatch[1].toLowerCase() === "urgente" ? "alta" : priorityMatch[1].toLowerCase())
        : "normal";
      await apiCall("post", "/tasks", {
        title: quickText.replace(/!(alta|urgente|baixa)/i, "").trim(),
        description: null,
        due_date: dueDate,
        priority,
      });
      setQuickText("");
      toast.success("Tarefa criada");
      load();
    } catch {
      toast.error("Erro ao criar");
    } finally {
      setAdding(false);
    }
  };

  const handleComplete = async (id) => {
    try {
      await apiCall("put", `/tasks/${id}`, { status: "concluido" });
      toast.success("Tarefa concluída");
      load();
    } catch {
      toast.error("Erro");
    }
  };

  const items = [...summary.overdue, ...summary.today, ...summary.no_date].slice(0, 6);

  return (
    <Card className="border border-border bg-card p-5 lg:p-6 shadow-soft rounded-md" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4" strokeWidth={1.5} style={{ color: "#0EA5E9" }} />
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-heading">{title}</div>
          {summary.total_pending > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary font-medium">{summary.total_pending} pendentes</span>
          )}
        </div>
        <Link to="/tarefas" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid={`${testId}-link`}>
          Ver todas <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
        </Link>
      </div>

      <form onSubmit={handleQuickAdd} className="flex gap-2 mb-3">
        <Input
          value={quickText}
          onChange={(e) => setQuickText(e.target.value)}
          placeholder="+ Tarefa rápida: ex: confirmar coloração =manu hoje 10h !alta"
          className="h-9 text-sm"
          data-testid={`${testId}-quick-input`}
          disabled={adding}
        />
        <Button type="submit" size="sm" disabled={adding || !quickText.trim()} data-testid={`${testId}-quick-add`}>
          <Plus className="h-3 w-3 mr-1" strokeWidth={1.5} /> Add
        </Button>
      </form>

      {loading ? (
        <div className="text-sm text-muted-foreground py-4">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4 text-center">
          Nada pendente ✨
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((t) => {
            const overdue = summary.overdue.some((x) => x.task_id === t.task_id);
            return (
              <div key={t.task_id} className="flex items-start gap-2 p-2 rounded-md hover:bg-secondary/40 transition-colors">
                <Checkbox checked={false} onCheckedChange={() => handleComplete(t.task_id)} className="mt-0.5" data-testid={`widget-task-${t.task_id}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{t.title}</span>
                    {t.assignee_name && (
                      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">= {t.assignee_name}</span>
                    )}
                    {overdue && (
                      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-100 text-red-700 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" strokeWidth={1.5} /> Atrasada
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                      {renderMentions(t.description, t.mentions_typed)}
                    </div>
                  )}
                  {t.due_date && (
                    <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Clock className="h-3 w-3" strokeWidth={1.5} />
                      {new Date(t.due_date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
